/**
 * Инструкция по роли и по странице (БЗ-01, БЗ-02): собирается из прав человека и описаний разделов портала.
 * В ней только то, что человек видит: разделы без его права не упоминаются, а разделы с деньгами при уровне
 * доступа ниже «Все деньги» не показываются вовсе, как и в меню. Цифр и сумм в тексте нет.
 */
import { canAnywhere, seesMoney } from './access';
import type { FieldBasisView } from './kb-basis';
import { BASIS_FIELDS } from './kb-basis';
import { MODULE_PERMISSION_PREFIXES } from './licensing';
import { MODULES, type ModuleCode, type PortalModule } from './modules';
import { PERMISSION_GROUPS } from './permissions';
import type { Access } from './schemas';
import type { HelpFaq, HelpLink, HelpSection, HelpView, RoleGuide } from './support';
import { MODULE_HELP, subPageFor } from './support-help-texts';

/** Разделы, которые целиком про деньги: без уровня «Все деньги» они не показываются в инструкции */
export const MONEY_MODULES: ModuleCode[] = ['budget', 'finance', 'analytics'];

/** Описания разделов без слов о деньгах — для тех, кто не видит суммы */
const PLAIN_DESCRIPTION: Partial<Record<ModuleCode, string>> = {
  portfolio: 'Все проекты и объекты на одном экране, блок «Требует внимания», сроки и состояние работ.',
  overview: 'Карточка проекта: объекты, вехи, сводка площадки.',
  procurement: 'Заявки, запросы КП, сравнение и выбор поставщика, реестры.',
  contracts: 'Договоры, согласование, графики поставок, исполнение.',
};

/** Права раздела по префиксам; для разделов вне тарифной карты — свои */
const EXTRA_PREFIXES: Partial<Record<ModuleCode, string[]>> = {
  portfolio: ['portfolio.'],
  overview: ['project.'],
  admin: ['admin.'],
  roadmap: ['roadmap.'],
};

const PERMISSION_TITLES = new Map<string, string>(
  PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => [p.code, p.title] as const)),
);

function prefixesOf(code: ModuleCode): string[] {
  return [...(MODULE_PERMISSION_PREFIXES[code] ?? []), ...(EXTRA_PREFIXES[code] ?? [])];
}

/** Что человеку можно делать в разделе: названия его прав на раздел */
export function youCanIn(access: Access, code: ModuleCode): string[] {
  const prefixes = prefixesOf(code);
  if (!prefixes.length) return [];
  const out: string[] = [];
  for (const [perm, title] of PERMISSION_TITLES) {
    if (prefixes.some((p) => perm.startsWith(p)) && canAnywhere(access, perm)) out.push(title);
  }
  return out;
}

/** Разделы, открытые человеку: по праву на раздел, без разделов с деньгами при уровне ниже «Все деньги» */
export function visibleModules(access: Access): PortalModule[] {
  return MODULES.filter(
    (m) => canAnywhere(access, m.permission) && (seesMoney(access) || !MONEY_MODULES.includes(m.code)),
  );
}

const GENERIC_FAQ: HelpFaq[] = [
  {
    q: 'Не вижу нужной кнопки или раздела',
    a: 'Кнопки и разделы показываются по вашим правам. Если действие нужно по работе, попросите администратора компании добавить право в вашу роль или напишите в поддержку.',
  },
  {
    q: 'Что-то не работает',
    a: 'Нажмите «Сообщить» вверху страницы и выберите «Ошибка». Если из-за этого стоит работа, отметьте «Не могу работать»: первый ответ человека — до 2 часов.',
  },
];

/** Раздел, к которому относится страница: самый длинный совпавший путь; «/» — только портфель */
export function moduleForPath(path: string): PortalModule | null {
  const clean = path.split(/[?#]/)[0] ?? '/';
  let best: PortalModule | null = null;
  for (const m of MODULES) {
    const hit = m.path === '/' ? clean === '/' : clean === m.path || clean.startsWith(`${m.path}/`);
    if (hit && (!best || m.path.length > best.path.length)) best = m;
  }
  return best;
}

/** Кто открывает страницу вне разделов: сотрудник, внешний участник, команда портала */
type Who = (ctx: Pick<HelpContext, 'access' | 'member' | 'platform'>) => boolean;
const anyone: Who = () => true;
// Старые вызовы без member — сотрудник
const staffOnly: Who = (c) => c.member !== 'external';
const externalOnly: Who = (c) => c.member === 'external';

/** Страницы вне разделов портала (своя инструкция, не зависит от права на раздел) */
const PAGES: Array<{ prefix: string; title: string; summary: string; steps: string[]; who: Who }> = [
  {
    prefix: '/profile',
    title: 'Профиль',
    summary: 'Ваши данные, пароль и уведомления.',
    who: anyone,
    steps: [
      'В «Уведомления» выберите, получать ли утреннюю сводку, на какую почту и в Telegram, и в какой час.',
      'Подключённый Telegram и почта нужны, чтобы ответы поддержки приходили вам и туда.',
    ],
  },
  {
    prefix: '/support',
    title: 'Поддержка',
    summary: 'Обращения в поддержку и предложения по улучшению портала.',
    who: anyone,
    steps: MODULE_HELP.support.steps,
  },
  {
    prefix: '/start',
    title: 'С чего начать',
    summary: 'Первые шаги и порядок работы на каждый день по вашим правам.',
    who: anyone,
    steps: [
      'Список собран по вашим ролям: только то, что открыто вам, со ссылками в нужные разделы.',
      'Страница всегда открывается из меню пользователя. Экскурсию по разделам можно пройти заново оттуда же.',
    ],
  },
  {
    prefix: '/cost-articles',
    title: 'Статьи расходов',
    summary: 'Утверждённые статьи финансового плана.',
    who: staffOnly,
    steps: [
      'Новая статья добавляется только запросом: финансовый менеджер проверяет, директор утверждает.',
      'Запрос на проверку и решение приходит в «Ждёт меня» тому, кто его принимает.',
    ],
  },
  {
    prefix: '/qhse/observe',
    title: 'Сообщить об опасности',
    summary: 'Наблюдение за безопасностью с фото.',
    who: staffOnly,
    steps: [
      'Сфотографируйте, что увидели, и коротко опишите. Можно отправить анонимно.',
      'Наблюдение уходит ответственному за охрану труда проекта.',
    ],
  },
  {
    prefix: '/schedule/contractor',
    title: 'Мой график',
    summary: 'Работы, где ваша организация — исполнитель.',
    who: externalOnly,
    steps: [
      'Видны план, базовый план и факт по вашим работам в графиках проектов, куда вас пригласили.',
      'Отметьте выполнение — генподрядчик проверит и примет его в график.',
    ],
  },
  {
    prefix: '/suppliers/cabinet',
    title: 'Кабинет поставщика',
    summary: 'Запросы коммерческих предложений, на которые вас пригласили.',
    who: (c) => externalOnly(c) && canAnywhere(c.access, 'procurement.view'),
    steps: [
      'Откройте запрос, заполните цены и сроки по строкам или приложите ответ файлом Excel или PDF.',
      'Если участвовать не будете — нажмите «Отказаться». Ответить можно до срока, указанного в запросе.',
    ],
  },
  {
    prefix: '/platform',
    title: 'Кабинет портала',
    summary: 'Компании портала, люди всех компаний, безопасность входа и объявления.',
    who: (c) => c.platform === true,
    steps: [
      'Компании: лицензия, люди и их активность, вход в компанию с режимом, сроком и причиной.',
      'Люди: поиск по имени и почте, блокировка входа, завершение сеансов, ссылка для нового пароля.',
      'Безопасность: блокировки перебора паролей и журнал входов. Объявления — компаниям письмом и в Telegram.',
      'Владельцу портала доступны ещё тарифы, заявки, счета, страны и рынки, продажи, команда портала, Pilot и версии.',
    ],
  },
  // Доска задач разработки (чат board, 03.11.2026): открыта не по роли, а владельцу портала и людям, которых он добавил
  {
    prefix: '/devboard',
    title: 'Доска задач разработки',
    summary: 'Задачи чатов Claude и команды портала: кто что взял в работу и когда.',
    who: staffOnly,
    steps: [
      'Доска открыта владельцу портала и людям, которых он добавил на вкладке «Доступ». Остальным она не видна.',
      'Перед работой задачу берут кнопкой «Взять»: взятую другим портал не отдаёт и называет, кто и когда её взял. Закончили или бросили — «Отпустить».',
      'Карточка открывается по названию задачи: поля, комментарии и поручения чатам. Если задачу изменили, пока вы её правили, сохранение не затрёт чужую правку — обновите и повторите.',
      'Чаты Claude работают с доской ключом: выпустите его на вкладке «Доступ» и положите в «Network secrets» окружения claude.ai/code — инструкция там же.',
    ],
  },
];

function staticPageFor(path: string) {
  let best: (typeof PAGES)[number] | null = null;
  for (const p of PAGES)
    if (
      (path === p.prefix || path.startsWith(`${p.prefix}/`)) &&
      (!best || p.prefix.length > best.prefix.length)
    )
      best = p;
  return best;
}

/** Страница вне разделов со своей инструкцией (профиль, поддержка, кабинеты): не зависит от права на раздел */
export function isStaticHelpPage(path: string): boolean {
  return staticPageFor(path.split(/[?#]/)[0] ?? '/') !== null;
}

/**
 * Есть ли у страницы своя инструкция (ПМ-11): страница вне разделов из списка выше или раздел с шагами.
 * По нему проверяется, что ссылка записи «Что нового» ведёт туда, где есть помощь.
 */
export function hasHelpFor(path: string): boolean {
  const clean = path.split(/[?#]/)[0] ?? '/';
  if (staticPageFor(clean)) return true;
  const mod = moduleForPath(clean);
  return Boolean(mod && MODULE_HELP[mod.code].steps.length);
}

/** Модуль подсказки поля → раздел портала (в справочнике полей «quality» — раздел «Качество и ОТ») */
const FIELD_MODULE: Record<string, ModuleCode> = { quality: 'qhse' };

/** Есть ли у раздела страницы поля с основанием: только тогда сервер читает основания из базы знаний */
export function helpNeedsFields(path: string): boolean {
  const mod = moduleForPath(path.split(/[?#]/)[0] ?? '/');
  return Boolean(mod && BASIS_FIELDS.some((f) => (FIELD_MODULE[f.module] ?? f.module) === mod.code));
}

/**
 * Подсказки полей раздела с основанием (БЗ-03): только поля, у которых администратор выбрал пункт регламента и
 * регламент не в архиве. Без основания подсказка не показывается — в форме её тоже нет.
 */
function fieldHints(code: ModuleCode, fields: FieldBasisView[] | undefined): string[] {
  if (!fields?.length) return [];
  const out: string[] = [];
  for (const f of BASIS_FIELDS) {
    if ((FIELD_MODULE[f.module] ?? f.module) !== code) continue;
    const b = fields.find((x) => x.fieldKey === f.key);
    if (!b?.articleId || b.archived) continue;
    const where = `${b.articleTitle ?? 'регламент'}${b.point ? `, п. ${b.point}` : ''}`;
    out.push(
      `${f.label}: ${f.what.toLowerCase()}. Основание — ${where}${b.pointText ? ` («${b.pointText}»)` : ''}.`,
    );
  }
  return out;
}

function moneyFree(m: PortalModule, access: Access): string {
  return seesMoney(access) ? m.description : (PLAIN_DESCRIPTION[m.code] ?? m.description);
}

function relatedLinks(access: Access, exclude: string | null): HelpLink[] {
  return visibleModules(access)
    .filter((m) => m.code !== exclude && m.group !== 'system')
    .slice(0, 6)
    .map((m) => ({ title: m.title, path: m.path }));
}

export interface HelpContext {
  path: string;
  access: Access;
  /** Названия ролей человека через запятую */
  roleNames: string;
  /** Pilot подключён и может отвечать (пока всегда нет) */
  pilotAvailable: boolean;
  /** Сотрудник или внешний участник (поставщик, подрядчик, заказчик); не задано — сотрудник */
  member?: 'staff' | 'external';
  /** Человек из команды портала: ему открыт кабинет портала */
  platform?: boolean;
  /** Основания полей компании (БЗ-03): из базы знаний, с учётом видимости статей */
  fields?: FieldBasisView[];
}

/** Инструкция к странице для роли пользователя (БЗ-01) */
export function buildHelp(ctx: HelpContext): HelpView {
  const clean = ctx.path.split(/[?#]/)[0] ?? '/';
  const roles = ctx.roleNames || 'роль не назначена';
  const page = staticPageFor(clean);
  const staticPage = page?.who(ctx) ? page : null;
  const mod = moduleForPath(clean);
  const open = mod ? visibleModules(ctx.access).some((m) => m.code === mod.code) : false;

  if (staticPage) {
    return {
      path: clean,
      title: staticPage.title,
      summary: staticPage.summary,
      roles,
      sections: [{ title: 'Как работать', items: staticPage.steps }],
      faq: GENERIC_FAQ,
      related: relatedLinks(ctx.access, null),
      pilot: { available: ctx.pilotAvailable },
    };
  }
  if (page || !mod || !open) {
    return {
      path: clean,
      title: 'Страница портала',
      summary:
        'Для этой страницы отдельной инструкции пока нет. Ниже — разделы, которые открыты вам, и способ быстро связаться с поддержкой.',
      roles,
      sections: [],
      faq: GENERIC_FAQ,
      related: relatedLinks(ctx.access, null),
      pilot: { available: ctx.pilotAvailable },
    };
  }
  const can = youCanIn(ctx.access, mod.code);
  const help = MODULE_HELP[mod.code];
  const sections: HelpSection[] = [];
  const sub = subPageFor(clean);
  if (sub && sub.path !== mod.path && (!sub.perm || canAnywhere(ctx.access, sub.perm)))
    sections.push({ title: `На этой странице: ${sub.title}`, items: sub.steps });
  if (can.length) sections.push({ title: 'Что вам здесь доступно', items: can });
  if (help.steps.length) sections.push({ title: 'Как работать', items: help.steps });
  const hints = fieldHints(mod.code, ctx.fields);
  if (hints.length) sections.push({ title: 'Подсказки полей', items: hints });
  return {
    path: clean,
    title: mod.title,
    summary: moneyFree(mod, ctx.access),
    roles,
    sections,
    faq: [...(help.faq ?? []), ...GENERIC_FAQ],
    related: relatedLinks(ctx.access, mod.code),
    pilot: { available: ctx.pilotAvailable },
  };
}

/** Инструкция по роли целиком: все разделы, открытые человеку (БЗ-02) */
export function buildRoleGuide(ctx: Pick<HelpContext, 'access' | 'roleNames'>): RoleGuide {
  const roles = ctx.roleNames || 'роль не назначена';
  const modules = visibleModules(ctx.access).map((m) => ({
    code: m.code,
    title: m.title,
    path: m.path,
    summary: moneyFree(m, ctx.access),
    youCan: youCanIn(ctx.access, m.code),
    steps: MODULE_HELP[m.code].steps,
  }));
  return {
    roles,
    intro: `Инструкция составлена по вашим правам: роль — ${roles}. В ней только те разделы, которые открыты вам в портале.${
      seesMoney(ctx.access) ? '' : ' Разделы с деньгами в инструкцию не входят: у вас нет права видеть суммы.'
    }`,
    modules,
  };
}

/** Шаги экскурсии по разделам роли (БЗ-03): приветствие, разделы, куда идти за помощью */
export interface TourStep {
  title: string;
  text: string;
  path: string | null;
}

export function buildTour(access: Access): TourStep[] {
  const mods = visibleModules(access).filter((m) => m.group !== 'system' || m.code === 'inbox');
  const steps: TourStep[] = [
    {
      title: 'Добро пожаловать в портал',
      text: 'Это короткая экскурсия по разделам, которые открыты вам. Её можно пропустить и вернуться к ней позже из меню пользователя или из «Помощи».',
      path: null,
    },
  ];
  for (const m of mods) steps.push({ title: m.title, text: moneyFree(m, access), path: m.path });
  steps.push({
    title: 'С чего начать',
    text: 'Первые шаги и порядок работы на каждый день — по вашим правам, со ссылками в нужные разделы. Страница всегда открывается из меню пользователя.',
    path: '/start',
  });
  steps.push({
    title: 'Помощь и поддержка',
    text: 'Кнопка «Сообщить» есть на каждой странице: ошибка, просьба о помощи или доработка — страница и снимок экрана приложатся сами, ответ придёт в «Ждёт меня». Кнопка «Помощь» рядом — инструкция к странице. Все обращения и предложения — в разделе «Поддержка».',
    path: '/support',
  });
  return steps;
}
